import React, { useEffect, useState } from 'react';
import axios, { CancelToken } from 'axios';
import PreviewClient from 'net/PreviewClient';
import { TrackingEvent } from 'react-hawksearch';
import { AutocompleteResponse } from './../../models/AutocompleteResponse';
import browserHistory from 'utils/history';

function limitStrLength(text, maxlength) {
	if (text.length > maxlength - 3) {
		return text.substring(0, maxlength).trimEnd() + '...';
	} else {
		return text;
	}
}

function getAtLeastOneExist(popular, categories, products, content) {
	const hasPopular = popular && popular.length > 0;
	const hasCategories = categories && categories.length > 0;
	const hasProducts = products && products.length > 0;
	const hasContent = content && content.length > 0;
	return hasPopular || hasCategories || hasProducts || hasContent;
}

enum SuggestType {
	PopularSearches = 1,
	TopCategories = 2,
	TopProductMatches = 3,
	TopContentMatches = 4,
}

function ProductsComponent({
	products,
	popular,
	ProductHeading,
	PopularHeading,
	getItemProps,
	redirectItemDetails,
	isAtleastOneExist,
	onViewMatches,
	downshift,
	searchedKeyword,
}) {
	return (
		<ul>
			{products && products.length ? (
				<>
					<h3>{ProductHeading}</h3>
					{products.map((item, index) => (
						<li
							{...getItemProps({
								item,
								index: `Product_${index}`,
								key: `Product_${index}`,
								onClick: () => {
									redirectItemDetails(item.Results.pageurl);
									TrackingEvent.track('autocompleteclick', {
										keyword: downshift.inputValue,
										suggestType: SuggestType.TopProductMatches,
										name: item.ProductName,
										url: item.Url,
									});
								},
							})}
						>
							<div dangerouslySetInnerHTML={{ __html: item.Thumb }} />
							<span className="p-sku">
								{item.Sku
									? item.Results.Document.itemtype[0] === 'Parent'
										? item.ProductName
										: item.Sku
									: null}
							</span>
							<span className="p-name">
								{item.Results.Document.itemtype[0] === 'Parent' &&
								item.Results.Document.description_short
									? limitStrLength(item.Results.Document.description_short[0], 150)
									: item.ProductName}
							</span>
						</li>
					))}
				</>
			) : null}
			{popular && popular.length ? (
				<>
					<h3>{PopularHeading}</h3>
					{popular.map((item, index) => (
						<li
							{...getItemProps({
								item,
								index: `Popular_${index}`,
								key: `Popular_${index}`,
								onClick: () => {
									redirectItemDetails(item.Url);
									TrackingEvent.track('autocompleteclick', {
										keyword: downshift.inputValue,
										suggestType: SuggestType.TopProductMatches,
										name: item.Value,
										url: item.Url,
									});
								},
							})}
						>
							<div dangerouslySetInnerHTML={{ __html: item.Value }} />
						</li>
					))}
				</>
			) : null}
		</ul>
	);
}

function CustomSuggestionListPreview({ downshift, searchResults, onViewMatches, isLoading }) {
	const {
		Popular: popular,
		Categories: categories,
		Products: products,
		Content: content,
		PopularHeading,
		CategoryHeading,
		ProductHeading,
		ContentHeading,
	} = searchResults;

	// const { actor, store } = useHawksearch();
	const [hoverKeyword, setHoverKeyword] = useState('');
	const [searchedKeyword, setSearchedKeyword] = useState('');
	const [hoverProducts, setHoverProducts] = useState([] as Array<{ Value: string; Url: string }>);
	const [hoverPopular, setHoverPopular] = useState([] as Array<{ Value: string; Url: string }>);
	const { getItemProps, getMenuProps, highlightedIndex, getInputProps } = downshift;
	const isAtleastOneExist = getAtLeastOneExist(popular, categories, products, content);
	useEffect(() => {
		const cts = axios.CancelToken.source();
		if (hoverKeyword) {
			doAutocomplete(hoverKeyword, cts.token);
		}
		return () => {
			cts.cancel();
		};
	}, [hoverKeyword]);

	async function doAutocomplete(input: string, cancellationToken?: CancelToken) {
		const component = document.getElementById('react-app-autocomplete');
		let baseAPI = '';
		let clientGUID = '';
		if (component) {
			baseAPI = component.getAttribute('data-base-api') || '';
			clientGUID = component.getAttribute('data-client-guid') || '';
		}
		setSearchedKeyword(input);
		const client = new PreviewClient({
			apiUrl: baseAPI,
			compareItemsURL: '',
			compareItemURL: '',
			addToCartURL: '',
		});

		let response: AutocompleteResponse | null = null;

		try {
			response = await client.autocomplete(
				{
					ClientGuid: clientGUID,
					Keyword: decodeURIComponent(input),
					IndexName: '',
					DisplayFullResponse: true,
					FacetSelections: {},
				},
				cancellationToken
			);
		} catch (error) {
			if (axios.isCancel(error)) {
				// if the request was cancelled, it's because this component was updated
				return;
			}

			console.error('Autocomplete request error:', error);
		}

		if (response) {
			setHoverProducts(response.Products);
			setHoverPopular(response.Popular);
		}
	}

	function setKeyword(keyword: string) {
		setHoverKeyword(keyword);
	}

	function redirectItemDetails(url: string) {
		browserHistory.push({
			pathname: url,
		});
	}
	return (
		<ul className="dropdown-menu autosuggest-menu__list" {...getMenuProps()}>
			{isLoading && <li className="autosuggest-menu__item loading-label">Loading...</li>}
			{hoverProducts && hoverProducts.length ? (
				<ProductsComponent
					products={hoverProducts}
					popular={hoverPopular}
					ProductHeading={ProductHeading}
					PopularHeading={PopularHeading}
					getItemProps={getItemProps}
					redirectItemDetails={redirectItemDetails}
					isAtleastOneExist={isAtleastOneExist}
					onViewMatches={onViewMatches}
					downshift={downshift}
					searchedKeyword={searchedKeyword}
				/>
			) : (
				<ProductsComponent
					products={products}
					popular={popular}
					ProductHeading={ProductHeading}
					PopularHeading={PopularHeading}
					getItemProps={getItemProps}
					redirectItemDetails={redirectItemDetails}
					isAtleastOneExist={isAtleastOneExist}
					onViewMatches={onViewMatches}
					downshift={downshift}
					searchedKeyword={searchedKeyword}
				/>
			)}
		</ul>
	);
}

export default CustomSuggestionListPreview;
